import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { 
  Shield, 
  Database, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  FileText, 
  Globe, 
  Lock, 
  Calendar,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff
} from 'lucide-react'
import { Graph } from '@/lib/api'

interface BreachAnalyticsProps {
  graph: Graph
  query: string
  profileData?: any
}

interface BreachData {
  breachName: string
  breachDate: string
  recordsAffected: number
  dataTypes: string[]
  exposedData: Record<string, any>
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  source: string
}

interface AttributeStats {
  type: string
  count: number
  icon: React.ReactNode
  color: string
  examples: string[]
}

export function BreachAnalytics({ graph, query, profileData }: BreachAnalyticsProps) {
  const [showSensitiveData, setShowSensitiveData] = useState(false)

  // Extract breach data from the graph or profileData
  const extractBreachData = (): BreachData[] => {
    const breaches: BreachData[] = []
    
    // First try to use profileData.breachSources if available (more reliable)
    if (profileData && profileData.breachSources && profileData.breachSources.length > 0) {
      console.log(`Extracting breach data from ${profileData.breachSources.length} breach sources`)
      
      profileData.breachSources.forEach((breach: any) => {
        const breachName = breach.source || 'Unknown Breach'
        
        // Aggregate all exposed data from all records in this breach
        const aggregatedExposedData: Record<string, any> = {}
        
        // If we have allRecords, aggregate data from all records
        if (breach.allRecords && Array.isArray(breach.allRecords) && breach.allRecords.length > 0) {
          // Collect all unique values for each field across all records
          const fieldValues: Record<string, Set<string>> = {}
          
          breach.allRecords.forEach((record: any) => {
            Object.keys(record).forEach(key => {
              if (record[key] && key !== 'Date' && key !== 'RegDate') {
                if (!fieldValues[key]) {
                  fieldValues[key] = new Set()
                }
                fieldValues[key].add(String(record[key]))
              }
            })
          })
          
          // Convert sets to arrays or single values
          Object.keys(fieldValues).forEach(key => {
            const values = Array.from(fieldValues[key])
            if (values.length === 1) {
              aggregatedExposedData[key] = values[0]
            } else if (values.length <= 5) {
              aggregatedExposedData[key] = values.join(', ')
            } else {
              aggregatedExposedData[key] = `${values.slice(0, 3).join(', ')} (+${values.length - 3} more)`
            }
          })
        } else {
          // Use direct breach fields
          aggregatedExposedData.email = breach.email || ''
          aggregatedExposedData.password = breach.password || ''
          aggregatedExposedData.fullName = breach.fullName || ''
          aggregatedExposedData.phone = breach.phone || ''
          aggregatedExposedData.address = breach.address || ''
          aggregatedExposedData.city = breach.city || ''
          aggregatedExposedData.birthDate = breach.birthDate || ''
          aggregatedExposedData.ipAddress = breach.ipAddress || ''
          aggregatedExposedData.gender = breach.gender || ''
          aggregatedExposedData.nationality = breach.nationality || ''
          aggregatedExposedData.jobTitle = breach.jobTitle || ''
          aggregatedExposedData.company = breach.company || ''
          aggregatedExposedData.website = breach.website || ''
          aggregatedExposedData.socialMedia = breach.socialMedia || ''
          aggregatedExposedData.avatar = breach.avatar || ''
        }
        
        // Determine risk level based on exposed data
        let riskLevel: 'low' | 'medium' | 'high' | 'critical' = 'low'
        const sensitiveFields = ['Password', 'SSN', 'CreditCard', 'BankAccount', 'Passport', 'DriverLicense']
        const hasSensitiveData = Object.keys(aggregatedExposedData).some(key => 
          sensitiveFields.some(field => key.toLowerCase().includes(field.toLowerCase()))
        )
        
        const dataFieldsCount = Object.keys(aggregatedExposedData).filter(k => aggregatedExposedData[k]).length
        
        if (hasSensitiveData) riskLevel = 'critical'
        else if (dataFieldsCount > 10) riskLevel = 'high'
        else if (dataFieldsCount > 5) riskLevel = 'high'
        else if (dataFieldsCount > 2) riskLevel = 'medium'
        
        const recordsAffected = parseInt(breach.records) || breach.numOfResults || breach.allRecords?.length || 1
        const dataTypes = breach.dataTypes ? breach.dataTypes.split(', ') : Object.keys(aggregatedExposedData).filter(k => aggregatedExposedData[k])
        
        breaches.push({
          breachName: breachName,
          breachDate: breach.date || 'Unknown Date',
          recordsAffected: recordsAffected,
          dataTypes: dataTypes.length > 0 ? dataTypes : ['Email, Password, Personal Data'],
          exposedData: aggregatedExposedData,
          riskLevel: riskLevel,
          source: breachName
        })
      })
      
      console.log(`Extracted ${breaches.length} breaches for analytics`)
      return breaches
    }
    
    if (!graph.nodes) return breaches

    // Find exposed data nodes - support both old and new entity structure
    const exposedDataNodes = graph.nodes.filter(node => {
      // Old structure: exposed_data nodes
      if (node.type === 'source' && node.label?.includes('Exposed Data:') && node.props?.raw_data) {
        return true
      }
      // New structure: entity nodes (person, email, phone, location, etc.)
      const entityType = node.props?.entity_type || ''
      if (entityType && ['person', 'email', 'phone', 'location', 'ip_address', 'document', 'password'].includes(entityType)) {
        return true
      }
      return false
    })

    exposedDataNodes.forEach(node => {
      // Extract breach info from edges if available
      let breachName = node.props?.breach_name || 'Unknown Breach'
      let breachDate = node.props?.breach_date || 'Unknown Date'
      let recordsAffected = node.props?.records_affected || 0
      
      // Try to find breach node from edges
      if (graph.edges) {
        const breachEdge = graph.edges.find((edge: any) => {
          const sourceNode = graph.nodes.find((n: any) => n.id === edge.source)
          const targetNode = graph.nodes.find((n: any) => n.id === edge.target)
          return (sourceNode === node && sourceNode?.props?.breach_name) ||
                 (targetNode === node && sourceNode?.props?.breach_name)
        })
        if (breachEdge) {
          const sourceNode = graph.nodes.find((n: any) => n.id === breachEdge.source)
          if (sourceNode?.props?.breach_name) {
            breachName = sourceNode.props.breach_name
            breachDate = sourceNode.props.breach_date || breachDate
            recordsAffected = sourceNode.props.records_affected || recordsAffected
          }
        }
      }
      
      // Build exposed data from entity props
      const entityType = node.props?.entity_type || ''
      const rawData: any = node.props.raw_data || {}
      
      // Extract entity-specific data
      if (entityType === 'email') {
        rawData.Email = node.props?.email_address || node.label
      } else if (entityType === 'phone') {
        rawData.Phone = node.props?.phone_number || node.label
      } else if (entityType === 'person') {
        rawData.FullName = node.props?.full_name || node.label
        if (node.props?.first_name) rawData.FirstName = node.props.first_name
        if (node.props?.last_name) rawData.LastName = node.props.last_name
      } else if (entityType === 'location') {
        if (node.props?.address) rawData.Address = node.props.address
        if (node.props?.city) rawData.City = node.props.city
        if (node.props?.state) rawData.State = node.props.state
        if (node.props?.country) rawData.Country = node.props.country
      } else if (entityType === 'ip_address') {
        rawData.IP = node.props?.ip_address || node.label
      } else if (entityType === 'document') {
        rawData[node.props?.document_type || 'ID'] = node.props?.document_number || node.label
      } else if (entityType === 'password') {
        rawData.Password = node.props?.password_hash || node.label
      }
      
      const dataTypes = Object.keys(rawData).length > 0 
        ? Object.keys(rawData).join(', ')
        : (node.props?.data_types || 'Email, Password, Personal Data').split(', ')
      
      // Determine risk level based on data sensitivity
      let riskLevel: 'low' | 'medium' | 'high' | 'critical' = 'low'
      const sensitiveFields = ['Password', 'SSN', 'CreditCard', 'BankAccount']
      const hasSensitiveData = Object.keys(rawData).some(key => 
        sensitiveFields.some(field => key.toLowerCase().includes(field.toLowerCase()))
      )
      
      if (hasSensitiveData) riskLevel = 'critical'
      else if (Object.keys(rawData).length > 5) riskLevel = 'high'
      else if (Object.keys(rawData).length > 2) riskLevel = 'medium'

      breaches.push({
        breachName,
        breachDate,
        recordsAffected,
        dataTypes: Array.isArray(dataTypes) ? dataTypes : [dataTypes],
        exposedData: rawData,
        riskLevel,
        source: node.source || 'Unknown'
      })
    })

    return breaches
  }

  const breachData = extractBreachData()

  // Calculate attribute statistics
  const calculateAttributeStats = (): AttributeStats[] => {
    const attributeMap = new Map<string, { count: number; examples: string[] }>()
    
    breachData.forEach(breach => {
      Object.entries(breach.exposedData).forEach(([key, value]) => {
        if (value && typeof value === 'string' && value.trim()) {
          const normalizedKey = key.toLowerCase()
          if (!attributeMap.has(normalizedKey)) {
            attributeMap.set(normalizedKey, { count: 0, examples: [] })
          }
          const stats = attributeMap.get(normalizedKey)!
          stats.count++
          if (stats.examples.length < 3) {
            stats.examples.push(String(value).substring(0, 50))
          }
        }
      })
    })

    const getAttributeInfo = (key: string): { icon: React.ReactNode; color: string; type: string } => {
      const lowerKey = key.toLowerCase()
      
      if (lowerKey.includes('email')) {
        return { icon: <Mail className="w-4 h-4" />, color: 'text-green-600', type: 'Email' }
      }
      if (lowerKey.includes('phone')) {
        return { icon: <Phone className="w-4 h-4" />, color: 'text-blue-600', type: 'Phone' }
      }
      if (lowerKey.includes('address') || lowerKey.includes('location')) {
        return { icon: <MapPin className="w-4 h-4" />, color: 'text-red-600', type: 'Address' }
      }
      if (lowerKey.includes('name') || lowerKey.includes('fullname') || lowerKey.includes('firstname') || lowerKey.includes('lastname')) {
        return { icon: <User className="w-4 h-4" />, color: 'text-purple-600', type: 'Name' }
      }
      if (lowerKey.includes('password')) {
        return { icon: <Lock className="w-4 h-4" />, color: 'text-yellow-600', type: 'Password' }
      }
      if (lowerKey.includes('ip')) {
        return { icon: <Globe className="w-4 h-4" />, color: 'text-cyan-600', type: 'IP Address' }
      }
      if (lowerKey.includes('date') || lowerKey.includes('time')) {
        return { icon: <Calendar className="w-4 h-4" />, color: 'text-orange-600', type: 'Date/Time' }
      }
      if (lowerKey.includes('doc') || lowerKey.includes('id') || lowerKey.includes('number')) {
        return { icon: <FileText className="w-4 h-4" />, color: 'text-indigo-600', type: 'Document' }
      }
      
      return { icon: <Database className="w-4 h-4" />, color: 'text-gray-600', type: 'Other' }
    }

    return Array.from(attributeMap.entries())
      .map(([key, stats]) => {
        const info = getAttributeInfo(key)
        return {
          type: info.type,
          count: stats.count,
          icon: info.icon,
          color: info.color,
          examples: stats.examples
        }
      })
      .sort((a, b) => b.count - a.count)
  }

  const attributeStats = calculateAttributeStats()

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'critical': return 'bg-red-500 text-white'
      case 'high': return 'bg-orange-500 text-white'
      case 'medium': return 'bg-yellow-500 text-black'
      case 'low': return 'bg-green-500 text-white'
      default: return 'bg-gray-500 text-white'
    }
  }

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'critical': return <AlertTriangle className="w-4 h-4" />
      case 'high': return <Shield className="w-4 h-4" />
      case 'medium': return <Database className="w-4 h-4" />
      case 'low': return <CheckCircle className="w-4 h-4" />
      default: return <Database className="w-4 h-4" />
    }
  }

  const maskSensitiveData = (value: string, type: string): string => {
    if (!showSensitiveData) {
      if (type.toLowerCase().includes('password')) {
        return '••••••••'
      }
      if (type.toLowerCase().includes('email')) {
        const [local, domain] = value.split('@')
        return `${local.substring(0, 2)}•••@${domain}`
      }
      if (type.toLowerCase().includes('phone')) {
        return value.replace(/\d(?=\d{4})/g, '•')
      }
    }
    return value
  }

  if (breachData.length === 0) {
    return (
      <Card className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-0 shadow-xl">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            Breach Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <TrendingUp className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-2">No Analytics Available</h3>
            <p className="text-gray-500 dark:text-gray-400">
              No breach data found for <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{query}</span>
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-0 shadow-xl">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              Breach Analytics
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSensitiveData(!showSensitiveData)}
              className="flex items-center gap-2"
            >
              {showSensitiveData ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showSensitiveData ? 'Hide' : 'Show'} Sensitive Data
            </Button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Detailed analysis of exposed data for <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{query}</span>
          </p>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg">
              <Database className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-blue-600">{breachData.length}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Total Breaches</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-lg">
              <TrendingUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-600">
                {breachData.reduce((sum, breach) => sum + breach.recordsAffected, 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Records Affected</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/30 rounded-lg">
              <AlertTriangle className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-orange-600">
                {breachData.filter(b => b.riskLevel === 'critical' || b.riskLevel === 'high').length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">High Risk</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-lg">
              <Shield className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-purple-600">{attributeStats.length}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Data Types</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attribute Statistics */}
      <Card className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Database className="w-6 h-6 text-purple-600" />
            Data Attribute Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {attributeStats.map((stat, index) => (
              <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-800 ${stat.color}`}>
                    {stat.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">{stat.type}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{stat.count} occurrences</p>
                  </div>
                </div>
                <div className="space-y-1">
                  {stat.examples.map((example, idx) => (
                    <div key={idx} className="text-xs text-gray-600 dark:text-gray-400 font-mono bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
                      {maskSensitiveData(example, stat.type)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Individual Breach Details */}
      {breachData.map((breach, index) => (
        <Card key={index} className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getRiskColor(breach.riskLevel)}`}>
                  {getRiskIcon(breach.riskLevel)}
                </div>
                {breach.breachName}
              </CardTitle>
              <Badge variant="outline" className={getRiskColor(breach.riskLevel)}>
                {breach.riskLevel.toUpperCase()}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-300">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {breach.breachDate}
              </span>
              <span className="flex items-center gap-1">
                <Database className="w-4 h-4" />
                {breach.recordsAffected.toLocaleString()} records
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(breach.exposedData).map(([key, value]) => {
                const info = attributeStats.find(stat => 
                  stat.type.toLowerCase().includes(key.toLowerCase()) || 
                  key.toLowerCase().includes(stat.type.toLowerCase())
                )
                return (
                  <div key={key} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {info?.icon || <Database className="w-4 h-4" />}
                      <span className="font-medium text-gray-900 dark:text-white capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
                      {maskSensitiveData(String(value), key)}
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
